// Endpoints comerciais — `/v1/metricas/comercial/*` (MET-12..MET-17).
//
// Valores monetários trafegam em CENTAVOS (`*Cents`); a view formata com
// `formatBRL`, que também espera centavos.
import { api } from '@/lib/api';
import type { Bucketed, Drilldown, MetricsFunnelDto, Segmented, WithEnvelope } from '../model';
import {
  metricsUrl,
  type MetricsListArgs,
  type MetricsQueryArgs,
  type MetricsSegmentArgs,
} from './shared';

/** Drill-down de uma etapa do funil (MET-15). */
export interface MetricsFunnelStageArgs extends MetricsListArgs {
  stageId: string;
}

export const metricsCommercialApi = api.injectEndpoints({
  endpoints: (build) => ({
    /** MET-12 — taxa de conversão. */
    metricsConversion: build.query<WithEnvelope<Segmented>, MetricsSegmentArgs>({
      query: (args) => metricsUrl('comercial/conversao', { ...args }),
      providesTags: ['MetricsAggregate'],
    }),
    /** MET-13 — conversão por faixa de tempo de primeira resposta. */
    metricsConversionByResponseTime: build.query<WithEnvelope<Bucketed>, MetricsQueryArgs>({
      query: (args) => metricsUrl('comercial/conversao-por-tmpr', { ...args }),
      providesTags: ['MetricsAggregate'],
    }),
    /** MET-14 — receita (fonte canônica: conclusionReason='SALE' + saleValueCents). */
    metricsRevenue: build.query<WithEnvelope<Segmented>, MetricsSegmentArgs>({
      query: (args) => metricsUrl('comercial/receita', { ...args }),
      providesTags: ['MetricsAggregate'],
    }),
    /** MET-15 — funil por etapa. */
    metricsFunnel: build.query<WithEnvelope<MetricsFunnelDto>, MetricsQueryArgs>({
      query: (args) => metricsUrl('comercial/funil', { ...args }),
      providesTags: ['MetricsAggregate'],
    }),
    metricsFunnelStageConversations: build.query<WithEnvelope<Drilldown>, MetricsFunnelStageArgs>({
      query: ({ stageId, ...args }) =>
        metricsUrl(`comercial/funil/${encodeURIComponent(stageId)}/conversas`, { ...args }),
      providesTags: ['MetricsDrilldown'],
    }),
    /** MET-16 — motivos de perda. */
    metricsLossReasons: build.query<WithEnvelope<Segmented>, MetricsSegmentArgs>({
      query: (args) => metricsUrl('comercial/motivos-perda', { ...args }),
      providesTags: ['MetricsAggregate'],
    }),
    /** MET-17 — origem do lead. */
    metricsOrigins: build.query<WithEnvelope<Segmented>, MetricsSegmentArgs>({
      query: (args) => metricsUrl('comercial/origens', { ...args }),
      providesTags: ['MetricsAggregate'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useMetricsConversionQuery,
  useMetricsConversionByResponseTimeQuery,
  useMetricsRevenueQuery,
  useMetricsFunnelQuery,
  useMetricsFunnelStageConversationsQuery,
  useMetricsLossReasonsQuery,
  useMetricsOriginsQuery,
} = metricsCommercialApi;
